import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { Search, Users, User, Settings, Waves, X, LayoutDashboard, Wallet, HandHeart, HeartHandshake, Ticket , Store , Receipt, CalendarDays } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { cn } from '@/lib/utils';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { getImageUrl } from '@/lib/imageUtils';

// Create a global state for the sidebar
let globalSetIsOpen = null;

export const openMobileSidebar = () => {
  if (globalSetIsOpen) {
    globalSetIsOpen(true);
  }
};

export const MobileSidebar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Register the global setter
  useEffect(() => {
    globalSetIsOpen = setIsOpen;
    return () => {
      globalSetIsOpen = null;
    };
  }, []);

  // Close on route change
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  // Prevent body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Mirrors the desktop sidebar so the two navigations cannot drift apart.
  const navItems = [
    { icon: LayoutDashboard, label: 'Dashboard', to: '/dashboard' },
    { icon: Search, label: 'Explore', to: '/' },
    { icon: HandHeart, label: 'Donate', to: '/donate' },
    { icon: Users, label: 'Connections', to: '/discover' },
    { icon: HeartHandshake, label: 'Causes', to: '/spaces' },
    { icon: Store, label: 'Businesses', to: '/businesses' },
    { icon: CalendarDays, label: 'Events', to: '/events' },
    { icon: Receipt, label: 'Your activity', to: '/my/activity' },
    { icon: Wallet, label: 'Wallet', to: '/wallet' },
    { icon: Ticket, label: 'Invites', to: '/invites' },
  ];

  // Phone-only entries: on desktop these live in the top-right avatar menu
  // and at the foot of the sidebar, neither of which exists here.
  const accountItems = [
    { icon: User, label: 'Profile', to: `/profile/${user?.username}` },
    { icon: Waves, label: 'Impact', to: '/impact' },
    { icon: Settings, label: 'Settings', to: '/settings' },
  ];


  if (!isOpen) return null;

  const content = (
    <div className="fixed inset-0 z-[100] lg:hidden">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={() => setIsOpen(false)}
      />
      
      {/* Sidebar Panel */}
      <div className="absolute left-0 top-0 bottom-0 w-72 bg-white dark:bg-zinc-900 shadow-2xl overflow-y-auto animate-slide-in-left">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-stone-200 dark:border-zinc-800">
          <div className="flex items-center gap-3">
            <Avatar className="w-10 h-10">
              <AvatarImage src={getImageUrl(user?.avatar_url)} className="object-cover" />
              <AvatarFallback className="bg-stone-200 dark:bg-zinc-700 text-sm">
                {user?.display_name?.charAt(0)?.toUpperCase() || 'U'}
              </AvatarFallback>
            </Avatar>
            <div>
              <p className="font-semibold text-stone-900 dark:text-white text-sm">
                {user?.display_name || 'User'}
              </p>
              <p className="text-xs text-stone-500 dark:text-zinc-400">
                @{user?.username || 'username'}
              </p>
            </div>
          </div>
          <button 
            onClick={() => setIsOpen(false)}
            className="p-2 rounded-full hover:bg-stone-100 dark:hover:bg-zinc-800"
          >
            <X className="w-5 h-5 text-stone-500 dark:text-zinc-400" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="p-3 space-y-1">
          {navItems.map(({ to, icon: Icon, label }) => (
            <NavLink
              key={label}
              to={to}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 px-3 py-3 rounded-xl transition-all text-sm',
                  isActive 
                    ? 'bg-stone-100 dark:bg-white/10 text-stone-900 dark:text-white font-medium' 
                    : 'text-stone-600 dark:text-zinc-400 hover:bg-stone-50 dark:hover:bg-white/5'
                )
              }
            >
              <Icon className="w-5 h-5" strokeWidth={1.5} />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>

        {/* Account */}
        <div className="px-3 pb-3 border-t border-stone-200 dark:border-zinc-800 pt-3">
          <div className="space-y-1">
            {accountItems.map(({ to, icon: Icon, label }) => (
              <NavLink
                key={label}
                to={to}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-3 px-3 py-3 rounded-xl transition-all text-sm',
                    isActive
                      ? 'bg-stone-100 dark:bg-white/10 text-stone-900 dark:text-white font-medium'
                      : 'text-stone-600 dark:text-zinc-400 hover:bg-stone-50 dark:hover:bg-white/5'
                  )
                }
              >
                <Icon className="w-5 h-5" strokeWidth={1.5} />
                <span>{label}</span>
              </NavLink>
            ))}
          </div>
        </div>

      </div>
    </div>
  );

  return createPortal(content, document.body);
};
